import { useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import type { ContextGenerationSnapshot } from '@renderer/hooks/use-context-generation-progress'
import { contextNotificationMockAtom, type ContextNotificationMock, type ContextNotificationRecord } from './state'

/** What the notification reads and writes: the user's record plus the generation snapshot. */
export interface ContextNotificationSource {
  record: ContextNotificationRecord | null
  setRecord: (update: (current: ContextNotificationRecord) => ContextNotificationRecord) => void
  snapshot: ContextGenerationSnapshot | null
  /** When the notification gives up on an unsettled run. Null never does. */
  settleBy: number | null
}

/** The mock walks through these so the bar's batch streaming is visible. */
const MOCK_STEPS = [0.1, 0.35, 0.6, 0.8]
const MOCK_STEP_MS = 9000

function mockSnapshot(progress: number): ContextGenerationSnapshot {
  const complete = progress >= 1
  return {
    integrations: [],
    completedIntegrations: 0,
    totalIntegrations: 0,
    liveContext: { status: complete ? 'complete' : 'pending' },
    progressPercent: progress * 100,
    updatedAt: new Date(0).toISOString(),
  }
}

/** A record edit folded back into the stored mode; dismissing ends the mock. */
function modeFromRecord(record: ContextNotificationRecord, complete: boolean): ContextNotificationMock | null {
  if (record.dismissed) return null
  if (complete) return 'finished'
  return record.minimized ? 'minimized' : 'progress'
}

/**
 * Developer-menu stand-in for the real source: a synthetic record and snapshot
 * shaped exactly like the real ones, so the hook that consumes them has no
 * mock branches. `null` when no mock is set.
 */
export function useContextNotificationMockSource(userId: string | undefined): ContextNotificationSource | null {
  const [mode, setMode] = useAtom(contextNotificationMockAtom)
  const stepping = mode === 'progress' || mode === 'minimized'
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (!stepping) return
    setStep(0)
    const id = setInterval(() => setStep((s) => (s + 1) % MOCK_STEPS.length), MOCK_STEP_MS)
    return () => clearInterval(id)
  }, [stepping])

  if (!mode || !userId) return null

  const complete = mode === 'finished'
  const record: ContextNotificationRecord = {
    eligible: true,
    minimized: mode === 'minimized',
    dismissed: false,
  }
  return {
    record,
    setRecord: (update) => setMode(modeFromRecord(update(record), complete)),
    snapshot: mockSnapshot(complete ? 1 : MOCK_STEPS[step]),
    settleBy: null,
  }
}
